import React, { useState, useEffect, useMemo } from 'react';
import { Search, ChevronLeft, BookOpen, Loader2, Info, CalendarDays, RefreshCcw, Hash, Book } from 'lucide-react';
import { useToast } from '../hooks/useToast';
import { fetchStoresWhereStaffAssigned, fetchGeneralJournalReport, currencyCodeToSymbolMap, client, salesSourceDocument, goodsReceiptDocument } from '../apiHelper';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import { useNavigate } from 'react-router-dom';
import Modal from '../components/ui/Modal';
import SalesSourceDocument from './SalesSourceDocument';
import GoodsReceiptDocument from './GoodsreceiptDocument';
import TrialModal from '../components/ui/TrialModal';

const formatDate = (date) => {
    if (!date) return '';
    return new Date(date).toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true // Set to false if you prefer 24-hour time
    });
};

const formatCurrency = (amount, currencyCode) => {
    const symbol = currencyCodeToSymbolMap[currencyCode] || '$';
    return `${symbol}${new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(amount)}`;
};

const GeneralJournalReport = ({ onBack }) => {
    const { showToast } = useToast();
    const [user, setUser] = useState(null);
    const [reportData, setReportData] = useState([]);
    const [orgname, setOrgName] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const navigate = useNavigate();

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isGoodsReceiptModalOpen, setIsGoodsReceiptModalOpen] = useState(false);

    const [currentReceipt, setCurrentReceipt] = useState(null);
    const [isFetchingReceipt, setIsFetchingReceipt] = useState(false);

    const [currentGoodsReceipt, setCurrentGoodsReceipt] = useState(null);
    const [isFetchingGoodsReceipt, setIsFetchingGoodsReceipt] = useState(false);

    // const [startDate, setStartDate] = useState(() => {
    //     const d = new Date(); d.setDate(d.getDate() - 30);
    //     return d.toISOString().split('T')[0];
    // });

    const [startDate, setStartDate] = useState(new Date().toISOString().split('T')[0]);
    const [endDate, setEndDate] = useState(new Date().toISOString().split('T')[0]);

    const [stores, setStores] = useState([]);
    const [selectedStoreId, setSelectedStoreId] = useState("");

    const [searchTerm, setSearchTerm] = useState("");
    const [isSearchExpanded, setIsSearchExpanded] = useState(false);

    const [showTrialModal, setShowTrialModal] = React.useState(true); // or use logic like localStorage to show once

    useEffect(() => {
        const lcUser = localStorage.getItem("user");
        if (lcUser) setUser(JSON.parse(lcUser));
    }, []);

    // const fetchReport = useMemo(() => {
    //     if (!user) return null;
    //     return async () => {
    //         setLoading(true);
    //         try {
    //             const params = { users_id: user.users_id, start_date: startDate, end_date: endDate };
    //             const response = await fetchGeneralJournalReport(params);
    //             // console.log(response)
    //             setOrgName(response.organization_name);
    //             setReportData(response.general_journal);
    //         } catch (err) {
    //             showToast("Failed to load report.", "error");
    //         } finally {
    //             setLoading(false);
    //         }
    //     };
    // }, [user, startDate, endDate, showToast]);

    const fetchReport = useMemo(() => {
        // Only fetch if user AND a store are selected
        if (!user || !selectedStoreId) return null;
        return async () => {
            setLoading(true);
            try {
                const params = {
                    users_id: user.users_id,
                    start_date: startDate,
                    end_date: endDate,
                    store_id: selectedStoreId // Added this
                };
                // console.log(params)
                const response = await fetchGeneralJournalReport(params);
                setOrgName(response.organization_name);
                setReportData(response.general_journal);
            } catch (err) {
                showToast("Failed to load report.", "error");
            } finally {
                setLoading(false);
            }
        };
    }, [user, startDate, endDate, selectedStoreId, showToast]); // Added selectedStoreId here

    useEffect(() => { if (fetchReport) fetchReport(); }, [fetchReport]);

    const { totalDebits, totalCredits, currencyCode } = useMemo(() => {
        let debits = 0, credits = 0, cc = 'USD';
        if (reportData.length > 0) cc = reportData[0].currency_code;
        reportData.forEach(t => {
            debits += t.total_debit || 0;
            credits += t.total_credit || 0;
        });
        return { totalDebits: debits, totalCredits: credits, currencyCode: cc };
    }, [reportData]);

    const isBalanced = Math.abs(totalDebits - totalCredits) < 0.01;

    const handlePurchaseOrder = async (po_number) => {
        if (!po_number) return;

        setIsFetchingGoodsReceipt(true);
        setIsGoodsReceiptModalOpen(true);

        try {
            const response = await goodsReceiptDocument({ po_number })
            // console.log(response)
            setCurrentGoodsReceipt(response)
        } catch (err) {
            const errorMessage = err.response?.data?.detail || "Error fetching goods receipt document"
            showToast(errorMessage, "error")
            setIsGoodsReceiptModalOpen(false);
        } finally {
            setIsFetchingGoodsReceipt(false);
        }
    }

    const handleOrderClick = async (orderId) => {
        if (!orderId || !user) return;

        setIsFetchingReceipt(true);
        setIsModalOpen(true);

        const payload = {
            users_id: user.users_id,
            orders_id: orderId
        };

        // console.log("Order Detail Request:", payload)
        try {
            const response = await salesSourceDocument(payload);
            // console.log(response)
            setCurrentReceipt(response)
        } catch (err) {
            const errorMessage = err.response?.data?.detail || "Error fetching source document";
            showToast(errorMessage, "error");
            setIsModalOpen(false);
        } finally {
            setIsFetchingReceipt(false);
        }
    }

    useEffect(() => {
        const loadStores = async () => {
            const lcUser = localStorage.getItem("user");
            if (!lcUser) return;
            const parsedUser = JSON.parse(lcUser);

            try {
                const fetchedStores = await fetchStoresWhereStaffAssigned(parsedUser);
                const mappedStores = fetchedStores.map(store => ({
                    label: store.identifier,
                    value: store.store_id,
                }));
                setStores(mappedStores);
                if (mappedStores.length > 0) {
                    setSelectedStoreId(mappedStores[0].value);
                }
            } catch (err) {
                showToast("Failed to load stores.", "error");
            }
        };
        loadStores();
    }, [showToast]);

    const filteredReportData = useMemo(() => {
        if (!searchTerm) return reportData;

        const lowerSearch = searchTerm.toLowerCase();
        return reportData.filter(transaction => {
            return (
                transaction.ref?.toLowerCase().includes(lowerSearch) ||
                transaction.description?.toLowerCase().includes(lowerSearch) ||
                // Search inside debits/credits for account names
                transaction.debits.some(d => d.account_name.toLowerCase().includes(lowerSearch)) ||
                transaction.credits.some(c => c.account_name.toLowerCase().includes(lowerSearch))
            );
        });
    }, [searchTerm, reportData]);

    return (
        <div className="bg-gray-100 min-h-screen pb-12">
            {/* 1. STICKY TOP HEADER */}
            <div className="bg-white border-b sticky top-0 z-20 shadow-sm px-4 py-4">
                <div className="max-w-5xl mx-auto flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="flex items-center gap-4">
                        <Button onClick={() => navigate(-1)} variant="ghost" className="p-2"><ChevronLeft size={20} /></Button>
                        <div>
                            <h1 className="text-xl font-bold text-gray-900 leading-tight">{orgname}</h1>
                            <p className="text-xs font-bold text-indigo-600 uppercase">General Journal</p>
                        </div>
                    </div>

                    <div className="flex items-center gap-2">
<div className="relative flex items-center justify-end">
    <div 
        className={`flex items-center bg-gray-50 border rounded-lg transition-all duration-300 ease-in-out ${
            isSearchExpanded ? 'w-64 px-3' : 'w-10 justify-center cursor-pointer hover:bg-gray-100'
        }`}
        onClick={() => !isSearchExpanded && setIsSearchExpanded(true)}
    >
        <Search 
            size={16} 
            className={`${isSearchExpanded ? 'text-indigo-500' : 'text-gray-500'}`} 
        />
        
        <input 
            type="text"
            placeholder="Search ref, customer, account..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onBlur={() => {
                // Only collapse if the search field is empty
                if (!searchTerm) setIsSearchExpanded(false);
            }}
            autoFocus={isSearchExpanded}
            className={`bg-transparent text-xs font-semibold outline-none transition-all duration-300 ${
                isSearchExpanded ? 'ml-2 w-full opacity-100' : 'w-0 opacity-0'
            } h-9`}
        />

        {/* Clear button appears only when there is text */}
        {searchTerm && isSearchExpanded && (
            <button 
                onClick={(e) => {
                    e.stopPropagation();
                    setSearchTerm("");
                }}
                className="text-gray-400 hover:text-gray-600"
            >
                <RefreshCcw size={12} /> {/* Or an X icon */}
            </button>
        )}
    </div>
</div>
                        <select
                            value={selectedStoreId}
                            onChange={(e) => setSelectedStoreId(e.target.value)}
                            className="bg-gray-50 border rounded-lg text-xs p-2 font-semibold outline-none focus:ring-2 focus:ring-indigo-500"
                        >
                            {stores.map(store => (
                                <option key={store.value} value={store.value}>
                                    {store.label}
                                </option>
                            ))}
                        </select>
                        <div className="flex bg-gray-50 border rounded-lg overflow-hidden">
                            <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="bg-transparent text-xs p-2 font-semibold outline-none border-r w-32" />
                            <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="bg-transparent text-xs p-2 font-semibold outline-none w-32" />
                        </div>
                        <Button onClick={fetchReport} disabled={loading} className="bg-black text-white px-4 h-9 rounded-lg">
                            {loading ? <Loader2 size={16} className="animate-spin" /> : <RefreshCcw size={16} />}
                        </Button>
                    </div>
                </div>
            </div>

            <div className="max-w-5xl mx-auto mt-6 px-4">
                {/* 2. TRANSACTION LIST */}
                {loading ? (
                    <div className="py-20 text-center"><Loader2 className="animate-spin inline mr-2" /> Loading records...</div>
                ) : (
                    <div className="space-y-6">
                        {filteredReportData.map((transaction, idx) => (
                            <div key={idx} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                                {/* Transaction Header */}
                                <div className="bg-gray-50 px-4 py-3 border-b flex flex-wrap justify-between items-center gap-2">
                                    <div className="flex items-center gap-3">
                                        <div className="bg-white px-2 py-1 rounded border shadow-sm text-xs font-bold text-gray-700">
                                            {formatDate(transaction.transaction_date)}
                                        </div>
                                        {/* <div className="flex items-center text-gray-500 font-mono text-xs">
                                            <Hash size={12} className="mr-1"/> {transaction.ref}
                                        </div> */}
                                        <div className="flex items-center font-mono text-xs">
                                            <Hash size={12} className="mr-1 text-gray-400" />

                                            {/* Condition 1: Sales Order / Source Document */}
                                            {transaction.orders_id ? (
                                                <button
                                                    onClick={() => handleOrderClick(transaction.orders_id)}
                                                    className="text-indigo-600 font-bold hover:underline hover:text-indigo-800 transition-colors cursor-pointer"
                                                >
                                                    {transaction.ref}
                                                </button>
                                            )
                                                /* Condition 2: Purchase Order */
                                                : transaction.transaction_type?.startsWith("PURCHASE_INVENTORY_") ? (
                                                    <button
                                                        onClick={() => handlePurchaseOrder(transaction.ref)}
                                                        className="text-emerald-600 font-bold hover:underline hover:text-emerald-800 transition-colors cursor-pointer"
                                                    >
                                                        {transaction.ref}
                                                    </button>
                                                )
                                                    /* Default: Plain Text */
                                                    : (
                                                        <span className="text-gray-500">{transaction.ref}</span>
                                                    )}
                                        </div>
                                    </div>
                                    <div className="text-xs font-medium text-gray-600 italic">
                                        {transaction.description}
                                    </div>
                                </div>

                                {/* Line Items Table */}
                                <table className="w-full text-sm">
                                    <thead>
                                        <tr className="text-[10px] uppercase text-gray-400 font-bold border-b tracking-wider">
                                            <th className="px-4 py-2 text-left font-bold">Account Description</th>
                                            <th className="px-4 py-2 text-right w-32">Debit</th>
                                            <th className="px-4 py-2 text-right w-32">Credit</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-50">
                                        {transaction.debits.map((p, i) => (
                                            <tr key={`d-${i}`} className="hover:bg-blue-50/30 transition-colors">
                                                <td className="px-4 py-3">
                                                    <div className="font-bold text-gray-900">{p.account_name}</div>
                                                    <div className="text-[10px] font-mono text-gray-400">{p.account_code}</div>
                                                </td>
                                                <td className="px-4 py-3 text-right font-mono font-bold text-gray-900">
                                                    {formatCurrency(p.amount, transaction.currency_code)}
                                                </td>
                                                <td className="px-4 py-3 text-right text-gray-200">—</td>
                                            </tr>
                                        ))}
                                        {transaction.credits.map((p, i) => (
                                            <tr key={`c-${i}`} className="hover:bg-blue-50/30 transition-colors">
                                                <td className="px-4 py-3 pl-10 border-l-4 border-indigo-500/10">
                                                    <div className="font-bold text-gray-700">{p.account_name}</div>
                                                    <div className="text-[10px] font-mono text-gray-400">{p.account_code}</div>
                                                </td>
                                                <td className="px-4 py-3 text-right text-gray-200">—</td>
                                                <td className="px-4 py-3 text-right font-mono font-bold text-indigo-600">
                                                    {formatCurrency(p.amount, transaction.currency_code)}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        ))}
                        {filteredReportData.length === 0 && !loading && (
                            <div className="py-20 text-center text-gray-500 bg-white rounded-xl border border-dashed">
                                No transactions found matching "{searchTerm}"
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* 3. FIXED BOTTOM TOTALS BAR */}
            <div className="fixed bottom-0 left-0 right-0 bg-gray-900 text-white shadow-2xl py-4 z-30">
                <div className="max-w-5xl mx-auto px-6 flex justify-between items-center">
                    <div className="flex items-center gap-3">
                        <div className={`w-3 h-3 rounded-full ${isBalanced ? 'bg-green-500 animate-pulse' : 'bg-red-500'}`} />
                        <span className="text-xs font-bold uppercase tracking-widest text-gray-400">
                            {isBalanced ? "Journal Balanced" : "Out of Balance"}
                        </span>
                    </div>
                    <div className="flex gap-8 md:gap-16">
                        <div className="text-right">
                            <p className="text-[10px] font-bold text-gray-500 uppercase">Total Debits</p>
                            <p className="font-mono text-lg font-bold">{formatCurrency(totalDebits, currencyCode)}</p>
                        </div>
                        <div className="text-right">
                            <p className="text-[10px] font-bold text-indigo-400 uppercase">Total Credits</p>
                            <p className="font-mono text-lg font-bold text-indigo-400">{formatCurrency(totalCredits, currencyCode)}</p>
                        </div>
                    </div>
                </div>
            </div>
            <Modal
                isOpen={isModalOpen}
                onClose={() => { setIsModalOpen(false); setCurrentReceipt(null); }}
                title="Source Document"
                size="wide"
            >
                <SalesSourceDocument receipt={currentReceipt} isLoading={isFetchingReceipt} formatDate={formatDate} formatCurrency={formatCurrency} />
            </Modal>
            <Modal
                isOpen={isGoodsReceiptModalOpen}
                onClose={() => { setIsGoodsReceiptModalOpen(false); setCurrentGoodsReceipt(null); }}
                title="Goods Receipt Document"
                size="wide"
            >
                <GoodsReceiptDocument data={currentGoodsReceipt} isLoading={isFetchingGoodsReceipt} formatDate={formatDate} formatCurrency={formatCurrency} />
            </Modal>
            {/* <TrialModal isOpen={showTrialModal} onRequestClose={() => setShowTrialModal(false)} /> */}
        </div>
    );
};

export default GeneralJournalReport;